import { z } from 'zod';
import type { SxProps, Theme } from '@mui/material/styles';
import type {
  DriveLabelPermission,
  LabelCopyMode,
  LabelDateFormat,
  LabelFieldType,
  LabelPermissionModel,
  LabelPermissionRole,
} from '../../../../api/drive-labels-api';

/** Limits from the Create Label design (Fields rules note). */
export const LABEL_LIMITS = {
  maxFields: 10,
  maxBadgeLists: 1,
  maxOptions: 200,
  maxMultiSelectValues: 10,
  maxPeople: 10,
  maxTextLength: 100,
  /**
   * Text length limits Google enforces (longer values are rejected). Most come from
   * limits.getLabel; the option description limit isn't listed there and comes from the Google
   * Admin console's label editor.
   */
  maxLabelNameLength: 40,
  maxLabelDescriptionLength: 255,
  maxFieldNameLength: 40,
  maxOptionNameLength: 40,
  maxOptionDescriptionLength: 140,
};

const tooLong = (what: string, max: number) =>
  `${what} can be at most ${max} characters`;

export const LABEL_FIELD_TYPES: LabelFieldType[] = [
  'BADGE_LIST',
  'SINGLE_SELECT',
  'MULTI_SELECT',
  'NUMBER',
  'DATE',
  'TEXT',
  'PERSON',
];

/** In the order shown in the "When copying files" dropdown (Figma applications_dropdown). */
export const COPY_MODES: LabelCopyMode[] = [
  'ALWAYS_COPY',
  'COPY_APPLIABLE',
  'DO_NOT_COPY',
];

export const COPY_MODE_LABELS: Record<LabelCopyMode, string> = {
  COPY_APPLIABLE: 'Copy label if user can apply',
  ALWAYS_COPY: 'Always copy label',
  DO_NOT_COPY: "Don't copy label",
};

export const COPY_MODE_DESCRIPTIONS: Record<LabelCopyMode, string> = {
  ALWAYS_COPY:
    "Label and values will be copied even if the user can't apply the label",
  COPY_APPLIABLE:
    'Label and values will be copied only if the user can apply the label',
  DO_NOT_COPY: "Label and values won't be copied",
};

export interface FieldTypeConfig {
  label: string;
  description: string;
  /**
   * Shown in both Drive and Gmail; otherwise Drive only. A label enabled for both apps can still
   * have Drive-only fields: Gmail users just don't see them.
   */
  gmailSupported: boolean;
  hasOptions: boolean;
  /** Label of the field card's "required" checkbox (Figma wording per field type). */
  requiredLabel: string;
}

export const FIELD_TYPE_CONFIG: Record<LabelFieldType, FieldTypeConfig> = {
  BADGE_LIST: {
    label: 'Badge List',
    description: 'Users will pick only one badge',
    gmailSupported: true,
    hasOptions: true,
    requiredLabel: 'Require users to pick an option',
  },
  SINGLE_SELECT: {
    label: 'Options List (Single Select)',
    description: 'Users will pick only one option',
    gmailSupported: true,
    hasOptions: true,
    requiredLabel: 'Require users to pick an option',
  },
  MULTI_SELECT: {
    label: 'Options List (Multi Select)',
    description: 'Users will pick multiple options',
    gmailSupported: false,
    hasOptions: true,
    requiredLabel: 'Require users to pick an option',
  },
  NUMBER: {
    label: 'Number',
    description: 'Users will enter a numerical value',
    gmailSupported: false,
    hasOptions: false,
    requiredLabel: 'Require users to enter a number',
  },
  DATE: {
    label: 'Date',
    description: 'Users will pick a calendar date',
    gmailSupported: false,
    hasOptions: false,
    requiredLabel: 'Require users to pick a date',
  },
  TEXT: {
    label: 'Text',
    description: 'Users will enter text in a text area',
    gmailSupported: false,
    hasOptions: false,
    requiredLabel: 'Require users to enter a text value',
  },
  PERSON: {
    label: 'Person',
    description: 'Users will pick a person from their contacts',
    gmailSupported: false,
    hasOptions: false,
    requiredLabel: 'Require users to pick a person',
  },
};

/** Date format choices, with the design's example dates. */
export const DATE_FORMAT_OPTIONS: { value: LabelDateFormat; label: string }[] =
  [
    { value: 'LONG_DATE', label: 'Long: September 18, 2026' },
    { value: 'SHORT_DATE', label: 'Short: 9/25/26' },
  ];

/** Badge colors from the design ("with fill - light" then "with fill - dark"). */
export const BADGE_COLORS = {
  light: ['#e5e7eb', '#90d9ba', '#c2dbfa', '#fdefc0', '#fbe2e2'],
  dark: ['#d98989', '#e6a817', '#2c7be5', '#4caf82', '#6b7280'],
};

/**
 * Text color for a badge chip, as in the design: white on the dark fills, near-black on the light
 * ones, regardless of the app's light/dark mode (the fill color doesn't change with the theme).
 */
export function badgeTextColor(color: string) {
  const value = color.toLowerCase();
  if (BADGE_COLORS.dark.includes(value)) {
    return '#ffffff';
  }
  return BADGE_COLORS.light.includes(value) ? 'rgba(0, 0, 0, 0.87)' : undefined;
}

/** Opens in a new window from the "Order matters for labels used in policy" info link. */
export const OPTION_ORDER_HELP_URL =
  'https://knowledge.workspace.google.com/admin/security/create-classification-labels-for-your-organization';

const optionSchema = z.object({
  /** Client-only key used for React keys and drag and drop; never sent to the server. */
  key: z.string(),
  id: z.string(),
  name: z
    .string()
    .trim()
    .min(1, 'Option name cannot be empty')
    .max(
      LABEL_LIMITS.maxOptionNameLength,
      tooLong('Option name', LABEL_LIMITS.maxOptionNameLength)
    ),
  description: z
    .string()
    .trim()
    .max(
      LABEL_LIMITS.maxOptionDescriptionLength,
      tooLong('Description', LABEL_LIMITS.maxOptionDescriptionLength)
    ),
  badgeColor: z.string(),
  /** Edit Label: the option is live in Google, so it has to be disabled before it's deleted. */
  published: z.boolean().optional(),
  /** Edit Label: the option is disabled, so it can't be selected any more. */
  disabled: z.boolean().optional(),
  /** Edit Label, disabled options: true keeps it visible but not selectable, false hides it. */
  showInApply: z.boolean().optional(),
  /** Edit Label: Google already has the option disabled, so it can be deleted. */
  savedDisabled: z.boolean().optional(),
});

const fieldSchema = z.object({
  key: z.string(),
  id: z.string(),
  type: z.enum(LABEL_FIELD_TYPES as [LabelFieldType, ...LabelFieldType[]]),
  name: z
    .string()
    .trim()
    .min(1, 'Field name cannot be empty')
    .max(
      LABEL_LIMITS.maxFieldNameLength,
      tooLong('Field name', LABEL_LIMITS.maxFieldNameLength)
    ),
  required: z.boolean(),
  dateFormat: z.enum(['LONG_DATE', 'SHORT_DATE']),
  multipleSelections: z.boolean(),
  /**
   * Edit Label: the field is live in Google, so it can't be deleted and its date format or person
   * setting can't change.
   */
  published: z.boolean().optional(),
  /** Edit Label: the field's name as saved in Google, used to confirm renames. */
  savedName: z.string().optional(),
  /** Edit Label: the field is disabled, so it can't be applied any more. */
  disabled: z.boolean().optional(),
  /** Edit Label: Google already has the field disabled, so it can be deleted. */
  savedDisabled: z.boolean().optional(),
  options: z
    .array(optionSchema)
    .max(
      LABEL_LIMITS.maxOptions,
      `A field can have at most ${LABEL_LIMITS.maxOptions} options`
    ),
});

function isValidUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export const labelInformationSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Label name is required')
    .max(
      LABEL_LIMITS.maxLabelNameLength,
      tooLong('Label name', LABEL_LIMITS.maxLabelNameLength)
    ),
  description: z
    .string()
    .trim()
    .max(
      LABEL_LIMITS.maxLabelDescriptionLength,
      tooLong('Label description', LABEL_LIMITS.maxLabelDescriptionLength)
    ),
  learnMoreUri: z
    .string()
    .trim()
    .refine((value) => value === '' || isValidUrl(value), {
      message: 'Enter a valid link starting with http:// or https://',
    }),
  driveEnabled: z.boolean(),
  gmailEnabled: z.boolean(),
  copyMode: z.enum(COPY_MODES as [LabelCopyMode, ...LabelCopyMode[]]),
});

export const labelFieldsSchema = z.object({
  fields: z
    .array(fieldSchema)
    .max(
      LABEL_LIMITS.maxFields,
      `You have reached the maximum limit of fields (${LABEL_LIMITS.maxFields})`
    ),
});

export const createLabelSchema = labelInformationSchema
  .extend(labelFieldsSchema.shape)
  .superRefine((values, ctx) => {
    const badgeLists = values.fields.filter((f) => f.type === 'BADGE_LIST');
    if (badgeLists.length > LABEL_LIMITS.maxBadgeLists) {
      ctx.addIssue({
        code: 'custom',
        path: ['fields'],
        message: `Maximum of ${LABEL_LIMITS.maxBadgeLists} badge list field is allowed`,
      });
    }
    values.fields.forEach((field, index) => {
      if (
        FIELD_TYPE_CONFIG[field.type].hasOptions &&
        field.options.length === 0
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['fields', index, 'options'],
          message: 'Add at least one option',
        });
      }
    });
  });

export type LabelOptionForm = z.infer<typeof optionSchema>;
export type LabelFieldForm = z.infer<typeof fieldSchema>;
export type CreateLabelForm = z.infer<typeof createLabelSchema>;

export const getCreateLabelDefaultValues = (): CreateLabelForm => ({
  title: '',
  description: '',
  learnMoreUri: '',
  driveEnabled: false,
  gmailEnabled: false,
  copyMode: 'COPY_APPLIABLE',
  fields: [],
});

/**
 * The permissions a new label starts with before it exists in Google: everyone in the organization
 * can apply it, which is what Google gives a label created in the Admin console.
 */
export const DEFAULT_LABEL_PERMISSIONS: LabelPermissionModel[] = [
  { principalType: 'AUDIENCE', email: '', role: 'APPLIER' },
];

/**
 * Shows permissions that haven't been saved to Google yet in the same shape Google returns, so the
 * preview can display them like saved ones.
 */
export function toPendingDisplayPermissions(
  permissions: LabelPermissionModel[]
): DriveLabelPermission[] {
  return permissions.map((permission) =>
    permission.principalType === 'AUDIENCE'
      ? { audience: 'audiences/default', role: permission.role }
      : {
          email: permission.email,
          role: permission.role,
          ...(permission.principalType === 'GROUP'
            ? { group: permission.email }
            : {}),
        }
  );
}

/**
 * How the organization-wide label permission reads, worded like the Google Admin console. Used by
 * the Label permissions dialog and the preview panel's organization avatar.
 */
const LABEL_PERMISSION_ROLES: readonly string[] = [
  'READER',
  'APPLIER',
  'ORGANIZER',
  'EDITOR',
];

/**
 * Keeps a role read from Google as it is, so saving permissions never changes access nobody
 * touched. Anything unrecognised (e.g. LABEL_ROLE_UNSPECIFIED) becomes the least access, READER.
 */
export function toLabelPermissionRole(
  role: string | null | undefined
): LabelPermissionRole {
  return role && LABEL_PERMISSION_ROLES.includes(role)
    ? (role as LabelPermissionRole)
    : 'READER';
}

/**
 * Converts Google's permissions to the shape the Label permissions dialog edits: the organization
 * entry (when there is one) and the users and groups with an email. Google's roles are kept as
 * they are, so saving doesn't change access nobody touched.
 */
export function fromGooglePermissions(
  permissions: DriveLabelPermission[]
): LabelPermissionModel[] {
  const audience = permissions.find((p) => p.audience);
  return [
    ...(audience
      ? [
          {
            principalType: 'AUDIENCE' as const,
            email: '',
            role: toLabelPermissionRole(audience.role),
          },
        ]
      : []),
    ...permissions
      .filter((p) => !p.audience && p.email)
      .map((p) => ({
        principalType: p.group ? ('GROUP' as const) : ('USER' as const),
        email: p.email ?? '',
        role: toLabelPermissionRole(p.role),
      })),
  ];
}

/**
 * Styles a dialog's Cancel like the page's secondary buttons (SharedButton "secondary"), so it reads
 * correctly in dark mode. GeneralDialog spreads this into its own sx object, so each value is set
 * per property rather than as one theme function.
 */
export const DIALOG_CANCEL_BUTTON_SX: SxProps<Theme> = {
  color: (theme) => theme.palette.semantic.button.secondary.text,
  borderColor: (theme) => theme.palette.semantic.button.secondary.border,
  '&:hover': {
    backgroundColor: (theme) => theme.palette.semantic.button.secondary.hover,
    borderColor: (theme) => theme.palette.semantic.button.secondary.border,
  },
};

export function orgPermissionSummary(
  role: 'READER' | 'APPLIER' | 'ORGANIZER' | 'EDITOR' | 'RESTRICTED',
  organizationName: string
) {
  switch (role) {
    case 'RESTRICTED':
      return 'Only people or groups added in advanced permissions can use this label';
    case 'READER':
      return `Anyone in ${organizationName} can view and search for this label on content they can view or comment on`;
    case 'ORGANIZER':
      return `Anyone in ${organizationName} can pin this label in shared drives they manage and add people who can apply it`;
    case 'EDITOR':
      return `Anyone in ${organizationName} can make any change to this label, including deleting it`;
    default:
      return `Anyone in ${organizationName} can view, apply, edit field values and search for this label on content they can edit`;
  }
}
